import {
  AlreadyStartedEnum,
  DeliveryIssueTypes,
  PolicyViolationPages,
} from '../models/types'
import { Direction, SlideButtons, SlideDecks } from './slide'

/** ***********
 * Generic enums
 *************/
export enum Countries {
  US = 'US',
  CA = 'CA',
  AU = 'AU',
}

export enum EventActions {
  Rendered = 'rendered',
  Submit = 'submit',
  Swiped = 'swiped',
  Clicked = 'clicked',
  Error = 'error',
  Error2FA = 'error-2fa',
  Success = 'success',
  Success2FA = 'success-2fa',
  Continue = 'continue',
  Close = 'close',
  Back = 'back',
  Previous = 'previous',
  Next = 'next',
  Exit = 'exit',
  Reload = 'reload',
}

/** ***********
 * Bank Account Form (Bank Onboarding)
 *************/

// BankAccountForm: Main Bank Account Form Rendered.
// NoBank: No bank account modal rendered.
// FindAccount: Find account number modal rendered
export enum BankAccountFormNamesRendered {
  BankAccountForm = 'bankaccountform',
  NoBank = 'nobank',
  FindAccount = 'findaccount',
}

export type BankAccountFormPropsRendered = {
  country: Countries
  name: BankAccountFormNamesRendered
}

export enum BankAccountFormNamesClicked {
  Close = 'close',
  GoBack = 'goback',
  Submit = 'submit',
  NoBank = 'nobank',
  FindAccount = 'findaccount',
}

export type BankAccountFormPropsClicked = {
  country: Countries
  name: BankAccountFormNamesClicked
}

export type BankAccountFormPropsSubmit = {
  country: Countries
  skipped?: boolean
  saved?: boolean
}

export type BankAccountFormPropsError = {
  country: Countries
  name: string
  error?: string
  data?: string | number
}

export const enum BankAdditionalVerificationPages {
  VerifyIdentity = 'verify_identity',
  VerifyAddress = 'verify_address',
  GovernmentId = 'government_id',
  SubmitPhotoId = 'submit_photo_id',
  SubmitPhotoIdBack = 'submit_photo_idback',
  VerificationInProgress = 'verification_in_progress',
  VerificationComplete = 'verification_complete',
  IdentificationNumber = 'identification_number',
}

export type BankAdditionalVerification = {
  country: Countries
  action: EventActions
  name?: string
  data?: string
  extra?: string
}

export enum PaymentAccountNames {
  GetStripeCredentials = 'get_stripe_credentials',
  CreatePaymentAccount = 'create_payment_account',
  GetPaymentAccountId = 'get_payment_account_id',
  UpdateBankAccount = 'update_bank_account',
  UpdatePaymentAccount = 'update_payment_account',
  ForwardPaymentAccount = 'forward_payment_account',
}

export type PaymentAccount = {
  action: EventActions
  name: PaymentAccountNames
  data?: string
  message?: string
}

/** ***********
 * Confirmation Page / Dropshipping Status
 *************/
export enum ConfirmationPageSteps {
  BackgroundCheck = 'backgroundcheck',
  PaymentAccountSetup = 'paymentaccountsetup',
  RedCardActivation = 'redcardactivation',
  FirstDelivery = 'firstdelivery',
}

/** ***********
 * Dasher Login Modal
 *************/
export type DasherLogin = {
  action: EventActions
  name?: string
  value?: string | boolean
  currentStep?: string
}

/** ***********
 * Dasher Apply Forms
 *************/
export enum DasherApplyNames {
  GetStarted = 'getstarted',
  SignUp = 'signup',
}
/** ***********
 * Safety Course Sections
 *************/
export enum SafetyCourseSections {
  ActiveDasherSplash = 'activedashersplash',
  ConfirmationSection = 'confirmationsection',
  ConfirmationSplash = 'confirmationsplash',
  Course = 'course',
}

export enum AddressCollectionScreens {
  Splash = 'splash',
  Form = 'form',
  Confirmation = 'confirmation',
  Complete = 'complete',
}

export enum ActivatedNoFirstDashScreens {
  Splash = 'splash',
  Start = 'start',
  Heatmap = 'heatmap',
  Order = 'order',
  Support = 'support',
  Dropshipping = 'dropshipping',
  Dash = 'dash',
}

export type DasherApply = {
  action: EventActions
  name: DasherApplyNames
  value?: string | number | object
  email?: string
  phoneNumber?: string
  zipCode?: string
}

export type InAppEducation = {
  title: string
  slide?: number
  deck?: SlideDecks
  button?: SlideButtons
  direction?: Direction
  action?: EventActions
}

export type SlideEvents = {
  title: string
  slide?: number
  deck?: SlideDecks
  button?: SlideButtons
  direction?: Direction
  action?: EventActions
}

export type DasherContinueApplication = {
  action: EventActions
  name?: AlreadyStartedEnum
}

export enum FAQNames {
  Opened = 'opened',
  Closed = 'closed',
}

export type LandingPageFAQ = {
  action: EventActions
  name: FAQNames
  faqTitle: string
}

export enum InsuranceTermsModalActions {
  Rendered = 'rendered',
  Clicked = 'clicked',
}
export type InsuranceTermsModal = {
  action: InsuranceTermsModalActions
  data?: any
}

export enum BackgroundCheckPages {
  Vehicle = 'vehicle',
  BackgroundCheck = 'background_check',
  Disclosures = 'disclosures',
  VehicleExtra = 'vehicle_extra',
  BackgroundCheckDL = 'background_check_dl',
}

export type TrackBackgroundCheck = {
  page: BackgroundCheckPages
  action: EventActions
  value?: {}
}

export type IdentityVerificationEvents = {
  step: string
  status: string
  email?: string
}

export enum HeaderNames {
  LogInButton = 'login_button',
  HamburgerButton = 'hamburger_button',
  MenuOptionWhyDoorDash = 'menu_option_why_doordash',
  MenuOptionSignUpDetails = 'menu_option_signup_details',
  MenuOptionHowToDash = 'menu_option_how_to_dash',
  MenuOptionFAQ = 'menu_option_faq',
  GetStartedCTA = 'get_started_cta',
}

export type LandingPageHeader = {
  action: EventActions
  name: HeaderNames
}

export enum SignUpDetailsTitles {
  Requirements = 'requirements',
  HowToSignUp = 'how_to_sign_up',
}

export enum SignUpDetailsNames {
  Opened = 'opened',
  Closed = 'closed',
}

export type LandingPageSignUpDetails = {
  action: EventActions
  name: SignUpDetailsNames
  title: SignUpDetailsTitles | string
}

export enum FastPayNames {
  FastPayOnboardingStart = 'fast_pay_onboarding_start',
  FastPayOnboardingSkip = 'fast_pay_onboarding_skip',
  CardInfoFields = 'card_info_fields',
  AddDebitCard = 'add_debit_card',
  FastPayInfoModal = 'fast_pay_info_modal',
  CvcInfoModal = 'cvc_info_modal',
  FastPayPage = 'fast_pay_page',
}

export enum FastPayValues {
  Opened = 'opened',
  Closed = 'closed',
  DebitCardNumber = 'debit_card_number',
  ExpDate = 'exp_date',
  CvcNumber = 'cvc_number',
  Submit = 'submit',
  Cancel = 'cancel',
}

export type FastPaySetup = {
  action: EventActions
  name: FastPayNames
  value?: FastPayValues
}

export enum NeedsVehicleInfoNames {
  Splash = 'needs_vehicle_info_splash',
  Modal = 'needs_vehicle_info_modal',
  Form = 'needs_vehicle_info_form',
}

export type NeedsVehiclInfo = {
  action: EventActions
  name: NeedsVehicleInfoNames
}

export type APIError = {
  code: number
  msg?: string
}

export type GetSales = {
  phone: string
  error?: APIError
}

export type BoFWaitlist = {
  action: EventActions
}

// Schema for Dasher Policy Violation Events
export type PolicyViolation = {
  type?: DeliveryIssueTypes
  page: PolicyViolationPages
  action: EventActions
  data?: object
}

export enum WebviewMultiFactorAuthNames {
  ReadyPhone = 'ready_phone',
  UpdatePhone = 'update_phone',
  VerifyPhone = 'verify_phone',
  GetHelp = 'get_help',
  Closed = 'closed',
}

export type WebviewMultiFactorAuth = {
  action: EventActions
  name: WebviewMultiFactorAuthNames
  country: string
}

export enum SignupMultiFactorAuthNames {
  VerifyPhone = 'signup_verify_phone',
  GetHelp = 'signup_get_help',
  Closed = 'signup_closed',
}

export type SignupMultiFactorAuth = {
  action: EventActions
  name: SignupMultiFactorAuthNames
}

export type EditBGCInfo = {
  action: EventActions
  name?: EditBGCInfoNames
  data?: object
}

export enum EditBGCInfoNames {
  SubmitButton = 'submit_button',
  CloseButton = 'close_button',
  EditButton = 'edit_button',
}

export const enum IdentityVerificationPages {
  Failed = 'failed',
  NeedsReview = 'needs_review',
  Success = 'success',
  Completed = 'completed',
  ResumeInquiry = 'resume_inquiry',
}

export enum GDPRActions {
  Render = 'render',
  Close = 'close',
  MfaRender = '2fa-render',
  MfaSuccess = '2fa-success',
  MfaClose = '2fa-close',
  MfaFail = '2fa-fail',
  PreSubmission = 'pre-submission',
  Submission = 'submission',
  Error = 'error',
  Exit = 'exit',
}

export type GDPR = {
  action: GDPRActions
  name?: string
}

export type RTI = {
  action: GDPRActions
  name?: string
}

export type RTF = {
  action: GDPRActions
  name?: string
}

export enum ApplicationCompleteNames {
  ApplicationComplete = 'application_complete',
}

export enum PromotionsPageNames {
  GetStarted = 'get_started',
  AnimationContinue = 'animation_continue',
  AnimationLoadTime = 'animation_load_time',
  PromotionCampaigns = 'promotion_campaigns',
  HourlyPay = 'hourly_pay',
}

export enum DownloadAppNames {
  DownloadApp = 'download_app',
}

export enum AddBankAccountNames {
  AddBankAccount = 'add_bank_account',
  AddLater = 'add_later',
  NoBankAccountModal = 'no_bank_account_modal',
  GoBack = 'go_back',
  DasherDirectLearnMore = 'dasher_direct_learn_more',
}

export enum PromotionsProfileSetupNames {
  HourlyPay = 'hourly_pay',
  PeakPay = 'peak_pay',
  PromotionsNotLoaded = 'promotions_not_loaded',
  PromotionViewed = 'promotion_viewed',
}
